import 'config'
import './datadogRum'
import './utils/nativeEventBridge'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { reloadPage } from 'utils/location'
import { makeReactResilientToGoogleTranslateDOMMutations } from 'utils/makeReactResilientToGoogleTranslateDOMMutations'

// Prefetch the main app bundle
let alreadyPrefetched = false
const prefetchMainAppBundle = (): void => {
  if (alreadyPrefetched) return
  alreadyPrefetched = true

  // These are the same imports that renderMainApp() uses.
  // By importing them here, the browser will fetch the chunks and cache them.
  void Promise.all([
    import('./App'),
    import('screens/TabRoot/TabRoot'),
    import('screens/TabRoot/TabRootStore'),
    import('react-query'),
    import('./queryClient'),
    import('components/ServiceWorkerUpdateModal/ServiceWorkerUpdateStore'),
    import('components/ServiceWorkerUpdateModal/ServiceWorkerUpdateModal'),
    import('utils/isPwa'),
    import('utils/isIFrame'),
  ]).catch((err) => {
    // Log prefetch errors for debugging but don't block the signup flow
    // eslint-disable-next-line no-console
    console.warn('[prefetch] Failed to prefetch main app:', err)
  })
}

// Expose prefetch function globally so SignupFlow can call it
// This is necessary because SignupFlow is in a separate bundle
;(window as any).__prefetchMainApp = prefetchMainAppBundle

const renderSignup = async (root: any) => {
  const { default: SignupRootLite } = await import('./SignupRootLite')
  root.render(
    <Sentry.ErrorBoundary>
      <SignupRootLite />
    </Sentry.ErrorBoundary>,
  )
}

const renderMainApp = async (root: any) => {
  const [{ default: App }, { TabRoot }, { TabRootProvider }, { QueryClientProvider }] = await Promise.all([
    import('./App'),
    import('screens/TabRoot/TabRoot'),
    import('screens/TabRoot/TabRootStore'),
    import('react-query'),
  ])
  const [{ default: queryClient }] = await Promise.all([import('./queryClient')])
  const [{ ServiceWorkerUpdateProvider }] = await Promise.all([
    import('components/ServiceWorkerUpdateModal/ServiceWorkerUpdateStore'),
  ])
  const [{ ServiceWorkerUpdateModal }] = await Promise.all([
    import('components/ServiceWorkerUpdateModal/ServiceWorkerUpdateModal'),
  ])
  const [{ useIsPwa }] = await Promise.all([import('utils/isPwa')])
  const [{ isIFrame }] = await Promise.all([import('utils/isIFrame')])

  const AppOrTabRoot: React.FC<React.PropsWithChildren<unknown>> = () => {
    const isPwa = useIsPwa()
    const isIframe = isIFrame()
    if (!isPwa || isIframe) {
      return <App />
    }
    return (
      <TabRootProvider>
        <QueryClientProvider client={queryClient}>
          <TabRoot />
        </QueryClientProvider>
      </TabRootProvider>
    )
  }

  root.render(
    <Sentry.ErrorBoundary>
      <AppOrTabRoot />
      <ServiceWorkerUpdateProvider>
        <ServiceWorkerUpdateModal />
      </ServiceWorkerUpdateProvider>
    </Sentry.ErrorBoundary>,
  )
}

const setupPrefetchOnButtonInteraction = () => {
  const handleHover = (event: PointerEvent) => {
    const target = event.target as HTMLElement | null
    if (target?.closest('button')) {
      cleanupListeners()
      prefetchMainAppBundle()
    }
  }

  const handleFocus = (event: FocusEvent) => {
    const target = event.target as HTMLElement | null
    if (target?.closest('button')) {
      cleanupListeners()
      prefetchMainAppBundle()
    }
  }

  const cleanupListeners = () => {
    document.removeEventListener('pointerover', handleHover, true)
    document.removeEventListener('focusin', handleFocus, true)
  }

  const attachListeners = () => {
    document.addEventListener('pointerover', handleHover, true)
    document.addEventListener('focusin', handleFocus, true) // keyboard accessibility
  }

  if (document.readyState === 'complete') {
    attachListeners()
  } else {
    window.addEventListener('load', attachListeners, { once: true })
  }
}

makeReactResilientToGoogleTranslateDOMMutations()

if (navigator.cookieEnabled) {
  const container = document.getElementById('root')
  const root = createRoot(container!) // eslint-disable-line @typescript-eslint/no-non-null-assertion

  // Tear down any previously-installed SW + its precache on every route
  // (including /signup, where register-service-worker isn't loaded). Without
  // this, stale SWs on long-lived domains like review apps keep serving old
  // HTML on soft refreshes.
  if ('serviceWorker' in navigator && localStorage.getItem('canInstallServiceWorker') !== 'yes') {
    navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister()))
    if (typeof caches !== 'undefined') {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)))
    }
  }

  // eslint-disable-next-line no-restricted-properties
  const isSignupRoute = window.location.pathname === '/signup'
  if (isSignupRoute) {
    renderSignup(root)
    setupPrefetchOnButtonInteraction()
  } else {
    renderMainApp(root)
    // Lazy load service worker registration to keep signup bundle light (avoids MobX)
    import('./register-service-worker').then(({ registerServiceWorker }) => {
      registerServiceWorker()
    })
  }
}

const INACTIVITY_INTERVAL_MS = 2 * 60 * 60 * 1000 // 2 hours
const RELOAD_THRESHOLD_MS = 24 * 60 * 60 * 1000 // 24 hours
const LOADED_AT = new Date()

let inactivityTimeout: any = null
const resetInactivityTimer = () => {
  if (inactivityTimeout) clearTimeout(inactivityTimeout)

  inactivityTimeout = setInterval(() => {
    if (new Date().getTime() - LOADED_AT.getTime() >= RELOAD_THRESHOLD_MS) {
      clearTimeout(inactivityTimeout)
      reloadPage()
    }
  }, INACTIVITY_INTERVAL_MS)
}

window.onload = resetInactivityTimer
window.onmousemove = resetInactivityTimer
window.onmousedown = resetInactivityTimer
window.ontouchstart = resetInactivityTimer
window.onclick = resetInactivityTimer
window.onkeydown = resetInactivityTimer
window.addEventListener('scroll', resetInactivityTimer, true)
